import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import loadLocale from './locales/loadLocale';
import {
  FALLBACK_LANGUAGES,
  LANGUAGES,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGE_CODES,
  activeLanguage,
  applyDocumentLanguage,
  normalizeLanguage,
  normalizeStoredLanguage,
} from './config/languages';

// Visitors from before language normalisation may have "en-US" stored.
// Rewrite it to the base code (or drop an unsupported one) before detection.
normalizeStoredLanguage();

// <html lang> and <html dir> follow the language actually rendered, also on
// the initial detection (init fires languageChanged too). ar, fa -> dir="rtl";
// ar and fa also get their web font (config/languages.js).
i18n.on('languageChanged', () => applyDocumentLanguage(activeLanguage(i18n)));

// Locale JSON is loaded LAZILY so it doesn't bloat the main bundle: each
// language (base file + its fragments, see locales/README.md) is fetched on
// demand when it becomes active, together with its fallback chain
// (FALLBACK_LANGUAGES: az -> tr, en; kk -> ru, en; ar -> en; ...).
i18n
  .use(
    resourcesToBackend((language, namespace, callback) => {
      loadLocale(language)
        .then((res) => callback(null, res))
        .catch((err) => callback(err, null));
    })
  )
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Per-language chains, `default` (tr) for en/ja/fa/ru and unknown codes.
    fallbackLng: FALLBACK_LANGUAGES,
    supportedLngs: SUPPORTED_LANGUAGE_CODES,
    nonExplicitSupportedLngs: true,
    // Never try to load "en-US.json"; regional variants use the base file.
    load: 'languageOnly',
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      // "en-US" -> "en", so i18n.language, the stored value and the switcher
      // all use base codes. Unsupported tags pass through and fall back.
      convertDetectedLanguage: (lng) => normalizeLanguage(lng) || lng,
    },
    // Default namespace must match what the loader callback returns
    ns: ['translation'],
    defaultNS: 'translation',
    debug: false,
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    // Many module pages call t('modules.X.cardKey') where the locale entry
    // is a nested object { title, subtitle, ... } rather than a flat string.
    // i18next ≥25 returns the literal "key 'X' returned an object instead
    // of string" error in that case. Until the locale JSONs are flattened,
    // transparently pick the .title field; otherwise fall back to the last
    // segment of the dotted key.
    returnedObjectHandler: (key, value) => {
      if (value && typeof value === 'object' && typeof value.title === 'string') {
        return value.title;
      }
      const segments = (key || '').split('.');
      return segments[segments.length - 1] || key || '';
    },
    react: {
      // Suspense while the active locale fetches on first render. Without this
      // the app would render with [missing] placeholders for the half-second
      // it takes to fetch the chunk.
      useSuspense: true,
    },
  });

// i18next builds Intl.PluralRules from the base code, but `pt` is European
// Portuguese here: 0 takes the plural form in pt-PT and the singular in
// pt / pt-BR. Map the code to the language's `pluralLocale` before the rule
// is built (the plural categories, one/many/other, are the same).
const PLURAL_LOCALES = Object.fromEntries(
  LANGUAGES.filter((l) => l.pluralLocale).map((l) => [l.code, l.pluralLocale]),
);
const pluralResolver = i18n.services && i18n.services.pluralResolver;
if (pluralResolver && typeof pluralResolver.getRule === 'function') {
  const getRule = pluralResolver.getRule.bind(pluralResolver);
  pluralResolver.getRule = (code, options) => getRule(PLURAL_LOCALES[code] || code, options);
}

export default i18n;
